import type { WorkspaceRecord } from "@/shared/domain/workspaces/WorkspaceRecord";
import type { WorkspaceService } from "@/shared/domain/workspaces/WorkspaceService";
import { ErrorMonitoringService } from "@/shared/services";
import type { State } from "@/shared/state/visualization";

/**
 * Create a new shared workspace, carrying over "public" accessibility from
 * an existing workspace when present.
 *
 * Always creates a new workspace so each share produces a unique, immutable
 * URL. Previously shared links remain unchanged.
 */
export async function createSharedWorkspace(
  workspaceService: WorkspaceService,
  vizState: State,
  orgId: string,
  existingWorkspaceId: string | undefined,
): Promise<WorkspaceRecord> {
  const [existingAccessibility, newRecord] = await Promise.all([
    existingWorkspaceId
      ? workspaceService
          .getWorkspaceAccessibility(existingWorkspaceId)
          .catch(() => null)
      : null,
    workspaceService.createWorkspace(vizState, orgId),
  ]);

  if (existingAccessibility?.workspace_accessibility === "public") {
    await workspaceService
      .updateWorkspaceAccessibility(newRecord.workspace_id, {
        accessibility: "public",
      })
      .catch((error: unknown) => {
        // Best-effort: if accessibility update fails, the workspace is still
        // usable — the owner can set accessibility manually in the modal.
        ErrorMonitoringService.captureError(error, {
          context: "createSharedWorkspace: failed to carry over accessibility",
          sourceWorkspaceId: existingWorkspaceId,
          newWorkspaceId: newRecord.workspace_id,
        });
      });
  }

  return newRecord;
}
